<template>
  <!-- Users page: who has signed in, whose invite never arrived, who is locked out (US 9365) -->
  <div class="us-page">
    <section class="home-hero us-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('maintenance_overview.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title" qid="users-page-title">{{ $t('users_page.title') }}</h1>
        <p class="home-hero__summary" qid="users-summary">{{ summaryText }}</p>

        <div class="home-search us-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="filters.keywordValue"
                 class="home-search__input"
                 :placeholder="$t('users_page.search_placeholder')"
                 :aria-label="$t('users_page.search_placeholder')"
                 autocomplete="off"
                 qid="users-search-input"
                 @input="debounceInput"
          />
          <span class="home-kbd">/</span>
        </div>

        <div class="home-hero__chips">
          <button v-if="isGranted(permissions.USER_CREATE)"
                  type="button"
                  class="home-chip home-chip--light"
                  qid="users-invite-button"
                  @click="$refs.invite.showInviteModal()"
          >
            <font-awesome-icon icon="plus"/>{{ $t('users_page.actions.invite') }}
          </button>
          <button v-if="canEdit"
                  type="button"
                  class="home-chip"
                  qid="users-merge-button"
                  @click="showMergeModal()"
          >
            <font-awesome-icon icon="exchange-alt"/>{{ $t('maintenance.users.merge_button') }}
          </button>
          <GuideButton v-if="getGuide('users_and_owners_list')" :guide="getGuide('users_and_owners_list')"></GuideButton>
        </div>
      </div>

      <div v-if="summary.all" class="home-week us-health" qid="users-health">
        <div class="home-week__top">
          <svg class="home-ring"
               width="96"
               height="96"
               viewBox="0 0 96 96"
               aria-hidden="true">
            <circle class="home-ring__track"
                    cx="48"
                    cy="48"
                    r="42"/>
            <circle v-if="summary.signed_in"
                    class="home-ring__value"
                    cx="48"
                    cy="48"
                    r="42"
                    :stroke-dasharray="ringDash(summary.signed_in, summary.all, 42)"
            />
          </svg>
          <b class="home-week__count us-health__count">{{ summary.signed_in }}/{{ summary.all }}</b>
          <div>
            <div class="home-week__title">{{ $t('users_page.health.title') }}</div>
            <div class="home-week__text">{{ $t('users_page.health.text', { done: summary.signed_in, total: summary.all }) }}</div>
          </div>
        </div>
        <button v-if="summary.problems"
                type="button"
                class="us-health__line"
                @click="setStatus('problems')">
          <span class="us-dot us-dot--red"></span>{{ $tc('users_page.health.problems', summary.problems, { count: summary.problems }) }}
        </button>
        <button v-if="summary.invited"
                type="button"
                class="us-health__line"
                @click="setStatus('invited')">
          <span class="us-dot us-dot--amber"></span>{{ $tc('users_page.health.invited', summary.invited, { count: summary.invited }) }}
        </button>
      </div>
    </section>

    <div class="us-bar">
      <div class="us-seg" role="tablist">
        <button v-for="status in statuses"
                :key="status"
                type="button"
                role="tab"
                :class="{ 'is-on': filters.status === status }"
                :aria-selected="filters.status === status ? 'true' : 'false'"
                :qid="'users-status-' + status"
                @click="setStatus(status)"
        >
          {{ $t('users_page.status.' + status) }}
          <em :class="{ 'is-red': status === 'problems' && summary.problems }">{{ summary[status] }}</em>
        </button>
      </div>
      <span class="us-grow"></span>
      <multiselect v-model="filters.role"
                   class="us-filter"
                   :options="roles"
                   track-by="id"
                   label="name_translated"
                   :placeholder="$t('users_page.filter.role')"
                   select-label="✔"
                   selected-label="✔"
                   deselect-label="x"
                   qid="users-filter-role"
                   @input="debounceInput"
      ></multiselect>
      <multiselect v-model="filters.authentication"
                   class="us-filter"
                   :options="signInOptions"
                   track-by="value"
                   label="text"
                   :placeholder="$t('users_page.filter.sign_in')"
                   select-label="✔"
                   selected-label="✔"
                   deselect-label="x"
                   qid="users-filter-sign-in"
                   @input="debounceInput"
      ></multiselect>
    </div>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table">
          <thead>
            <tr>
              <th v-for="column in columns"
                  :key="column.key"
                  :class="{ 'is-sortable': column.sort, 'us-table__last': column.key === 'last_login_at' }"
                  :aria-sort="column.sort && selectedSort === column.key ? (selectedDirection === 'asc' ? 'ascending' : 'descending') : null"
                  :qid="'users-sort-' + column.key"
                  @click="column.sort && selectSort(column.key)"
              >
                {{ $t('users_page.table.' + column.key) }}
                <font-awesome-icon v-if="column.sort && selectedSort === column.key"
                                   :icon="selectedDirection === 'asc' ? 'caret-up' : 'caret-down'"
                                   class="ml-1"
                />
              </th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in tableItems" :key="user.id" qid="users-row">
              <td>
                <div class="us-who">
                  <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(user) + ')' }"></span>
                  <span class="us-who__name" qid="users-name">{{ user.name }}</span>
                  <span v-if="isMe(user)" class="us-you">{{ $t('users_page.you') }}</span>
                </div>
              </td>
              <td class="us-muted" qid="users-email">{{ user.email }}</td>
              <td>
                <span v-if="roleOf(user)"
                      class="v2-tag"
                      :class="roleTag(roleOf(user))"
                      qid="users-role">
                  {{ $t('maintenance.users.role.' + $options.filters.slugify(roleOf(user).name)) }}
                </span>
              </td>
              <td>
                <span class="us-method">
                  <SignInMethodIcon :method="user.authentication || 'Application'"/>
                  {{ $t('users_page.sign_in.' + (user.authentication || 'Application')) }}
                </span>
              </td>
              <td>
                <span class="us-lock">
                  <button v-if="canEdit && !isMe(user)"
                          type="button"
                          class="us-toggle"
                          :class="{ 'is-on': user.locked }"
                          role="switch"
                          :aria-checked="user.locked ? 'true' : 'false'"
                          :aria-label="$t('users_page.table.locked')"
                          qid="users-lock-toggle"
                          @click="lockSwitch(user, user.locked ? 0 : 1)"
                  ></button>
                  {{ user.locked ? $t('users_page.lock.locked') : $t('users_page.lock.active') }}
                </span>
              </td>
              <td>
                <div v-if="user.last_login_at"
                     class="us-last"
                     v-b-tooltip.hover
                     :title="formatDateTime(user.last_login_at)">
                  <span class="us-last__date">{{ formatDateTime(user.last_login_at) }}</span>
                  <span class="us-last__ago">{{ user.last_login_at | formatRelativeDate }}</span>
                </div>
                <div v-else class="us-pending">
                  <InviteStatus :invite="user.invite"/>
                  <button v-if="canResend"
                          type="button"
                          class="us-link"
                          qid="users-resend-invite"
                          @click="resendInvite(user)"
                  >
                    <font-awesome-icon icon="paper-plane"/>{{ $t('users_page.resend') }}
                  </button>
                </div>
              </td>
              <td class="us-table__actions">
                <button v-if="canEdit"
                        type="button"
                        class="us-icon-btn"
                        v-b-tooltip.hover
                        :title="$t('users_page.actions.edit')"
                        :aria-label="$t('users_page.actions.edit')"
                        qid="users-edit-button"
                        @click="openEdit(user)"
                >
                  <font-awesome-icon icon="pen"/>
                </button>
                <b-dropdown v-if="(canResend && !user.last_login_at) || canRemove(user)"
                            right
                            no-caret
                            variant="link"
                            toggle-class="us-icon-btn"
                            qid="users-more-button"
                >
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-h"/>
                    <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                  </template>
                  <b-dropdown-item v-if="canResend && !user.last_login_at" @click="resendInvite(user)">
                    <font-awesome-icon icon="paper-plane" class="mr-2"/>{{ $t('users_page.resend_invite') }}
                  </b-dropdown-item>
                  <b-dropdown-item v-if="canRemove(user)" variant="danger" @click="showConfirmDeleteModal(user)">
                    <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('users_page.actions.remove') }}
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row">
              <td colspan="7" class="us-empty">{{ $t('system.no_results') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="us-foot">
        <span class="us-foot__count">{{ getPerPage }}</span>
        <multiselect v-model="perPage"
                     class="us-filter us-filter--small"
                     :options="perPageOptions"
                     track-by="value"
                     label="value"
                     :allow-empty="false"
                     :placeholder="selectedPerPage"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="users-per-page"
                     @select="perPageChanged"
        ></multiselect>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      @change="pageChanged"
                      limit="5"
        ></b-pagination>
      </div>
    </div>

    <UserInviteModal ref="invite" buttonType="none" @reloadUserList="reload"></UserInviteModal>

    <UserEditPanel ref="edit"
                   :roles="roles"
                   :canResend="canResend"
                   :canRemove="!!editingUser && canRemove(editingUser)"
                   @saved="reload"
                   @resend="resendInvite"
                   @remove="showConfirmDeleteModal"
    ></UserEditPanel>

    <!-- Remove from workspace -->
    <b-modal ref="delete-user" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-user-title">{{ $t('users_page.remove.title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.users.delete.cancel')"
                  @click="hideConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(itemToDelete) + ')' }"></span>
        <div>
          <b>{{ itemToDelete.name }}</b>
          <div class="us-muted">{{ itemToDelete.email }}</div>
        </div>
      </div>
      <p class="mt-3 mb-0">{{ $t('users_page.remove.text', { workspace: getClient.name }) }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-user-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('administration.users.delete.cancel') }}
        </b-button>
        <b-button variant="danger" qid="delete-user-submit-button" @click="confirmDeleteUser(itemToDelete)">
          {{ $t('users_page.actions.remove') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Merge users: whose responsibilities move to whom, and what happens to the first user afterwards -->
    <b-modal ref="merge-users"
             size="lg"
             modal-class="v2-modal us-merge-modal"
             hide-footer
             @hide="hideMergeModal"
    >
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0" qid="merge-users-title">{{ $t('maintenance.users.merge_button') }}</h5>
            <p class="us-modal-head__sub">{{ $t('users_page.merge.subtitle') }}</p>
          </div>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.users.create.cancel')"
                  @click="hideMergeModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="merge-users-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit.prevent="mergeFormSubmit" class="us-form" qid="merge-users-form">
        <div class="us-merge">
          <div v-for="side in mergeSides"
               :key="side.key"
               class="us-merge__card"
               :class="'us-merge__card--' + side.key"
          >
            <span class="us-merge__label">{{ $t('users_page.merge.' + side.key + '_label') }}</span>
            <div class="us-merge__who">
              <span v-if="side.user"
                    class="us-avatar us-avatar--md"
                    :style="{ backgroundImage: 'url(' + avatarUrl(side.user) + ')' }"
              ></span>
              <span v-else class="us-merge__empty"><font-awesome-icon icon="user"/></span>
              <span class="us-merge__name">
                <b>{{ side.user ? side.user.name : $t('users_page.merge.pick_user') }}</b>
                <span v-if="side.user">{{ side.user.email }}</span>
              </span>
            </div>
            <multiselect :value="side.user"
                         :options="users"
                         :multiple="false"
                         track-by="slug"
                         label="name"
                         :placeholder="$t('users_page.merge.search_user')"
                         :select-label="$t('system.dropdown_select_label')"
                         :selected-label="$t('system.dropdown_selected_label')"
                         :deselect-label="$t('system.dropdown_deselect_label')"
                         :qid="'merge-' + side.key + '-user'"
                         @input="selectMergeUser(side.key, $event)"
                         @search-change="loadUsers"
            >
              <template slot="singleLabel" slot-scope="props">
                <span>{{ props.option.name }}</span>
              </template>
              <template slot="option" slot-scope="props">
                <span class="us-avatar us-avatar--xs" :style="{ backgroundImage: 'url(' + avatarUrl(props.option) + ')' }"></span>
                <span class="ml-2">{{ props.option.name }}</span>
              </template>
            </multiselect>
          </div>
          <span class="us-merge__arrow" aria-hidden="true"><font-awesome-icon icon="arrow-right"/></span>
        </div>

        <p class="us-merge__moves">
          <font-awesome-icon icon="info-circle" class="mr-1"/>
          {{ $t('users_page.merge.moves', { workspace: getClient.name }) }}
        </p>

        <div>
          <span class="us-field-label">{{ $t('users_page.merge.after_label') }}</span>
          <div class="us-choice" role="radiogroup">
            <label v-for="option in keepOptions"
                   :key="option.value"
                   class="us-choice__option"
                   :class="{ 'is-on': keepUser === option.value }"
                   :qid="'merge-keep-' + option.value"
            >
              <input v-model="keepUser"
                     type="radio"
                     name="merge-keep"
                     :value="option.value"
              >
              <span>
                <b>{{ option.title }}</b>
                <span>{{ option.text }}</span>
              </span>
            </label>
          </div>
        </div>

        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="merge-users-cancel-button" @click="hideMergeModal">
            {{ $t('administration.users.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    :variant="keepUser ? 'success' : 'danger'"
                    :disabled="buttonDisabled"
                    qid="merge-users-submit-button"
          >
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('users_page.merge.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import UserInviteModal from '@/components/user/UserInviteModal.vue';
  import UserEditPanel from '@/components/user/UserEditPanel.vue';
  import InviteStatus from '@/components/user/InviteStatus.vue';
  import SignInMethodIcon from '@/components/user/SignInMethodIcon.vue';

  const ROLE_TAGS = {
    'Client admin': 'v2-tag--violet',
    'Partition admin': 'v2-tag--violet',
    'Validator': 'v2-tag--blue'
  };

  export default {
    name: 'Users',
    components: {
      GuideButton,
      UserInviteModal,
      UserEditPanel,
      InviteStatus,
      SignInMethodIcon
    },
    data: function() {
      return {
        tableItems: [],
        summary: { all: 0, signed_in: 0, invited: 0, problems: 0, locked: 0 },
        statuses: ['all', 'signed_in', 'invited', 'problems', 'locked'],
        columns: [
          { key: 'name', sort: true },
          { key: 'email', sort: true },
          { key: 'role' },
          { key: 'sign_in' },
          { key: 'locked' },
          { key: 'last_login_at', sort: true }
        ],
        filters: {
          keywordValue: '',
          role: null,
          authentication: null,
          status: 'all'
        },
        selectedSort: '',
        selectedDirection: '',
        roles: [],
        editingUser: null,

        // Pagination
        perPageOptions: [
          { value: '10' },
          { value: '20' },
          { value: '50' },
          { value: '100' }
        ],
        perPage: null,
        selectedPerPage: '10',
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        selectedFirstUser: null,
        selectedSecondUser: null,
        keepUser: 1,
        users: [],
        error: '',
        loading: true,
        buttonDisabled: false,
        myGuides: []
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      getFilters() {
        return this.$store.state.activeFilters;
      },
      getStorePerPage() {
        return this.$store.state.perPage;
      },
      getClientRoles() {
        return this.$store.state.client.clientRoles;
      },
      summaryText() {
        let sentences = [this.$tc('users_page.summary.users', this.summary.all, { count: this.summary.all })];

        if (this.summary.problems) {
          sentences.push(this.$tc('users_page.summary.problems', this.summary.problems, { count: this.summary.problems }));
        }
        if (this.summary.invited) {
          sentences.push(this.$tc('users_page.summary.invited', this.summary.invited, { count: this.summary.invited }));
        }

        return sentences.join(' ');
      },
      mergeSides() {
        return [
          { key: 'from', user: this.selectedFirstUser },
          { key: 'to', user: this.selectedSecondUser }
        ];
      },
      // What happens to the user whose responsibilities move
      keepOptions() {
        let from = this.selectedFirstUser ? this.selectedFirstUser.name : this.$t('users_page.merge.from_fallback');
        let to = this.selectedSecondUser ? this.selectedSecondUser.name : this.$t('users_page.merge.to_fallback');

        return [
          { value: 1, title: this.$t('users_page.merge.keep_both'), text: this.$t('users_page.merge.keep_both_text', { from: from, workspace: this.getClient.name }) },
          { value: 0, title: this.$t('users_page.merge.keep_receiving', { to: to }), text: this.$t('users_page.merge.keep_receiving_text', { from: from, workspace: this.getClient.name }) }
        ];
      },
      canEdit() {
        return this.isGranted(this.permissions.USER_EDIT);
      },
      canResend() {
        return this.isGranted(this.permissions.USER_CREATE);
      },
      signInOptions() {
        return ['Microsoft', 'Google', 'Application'].map(value => ({ value: value, text: this.$t('users_page.sign_in.' + value) }));
      },
      getPerPage() {
        let from = (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
        let to = Math.min(from + Number(this.selectedPerPage) - 1, this.totalRows);

        return this.totalRows ? this.$t('users_page.pagination', { from: from, to: to, total: this.totalRows }) : '';
      }
    },
    watch: {
      // Reload when the workspace changes
      getClient: function () {
        this.setRoles();
        this.reload();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent();
      }, 400),
      reload() {
        this.loadContent(this.currentPage);
        this.loadSummary();
      },
      // The 120px photo when the list sent it, so avatars stay sharp at 34-48px
      avatarUrl(user) {
        return userAvatarUrl(user, true);
      },
      isMe(user) {
        return this.$store.state.user && user.id === this.$store.state.user.id;
      },
      roleOf(user) {
        return _.find(user.userRoles, { 'client': this.getClient.id });
      },
      roleTag(role) {
        return ROLE_TAGS[role.name] || 'v2-tag--grey';
      },
      canRemove(user) {
        return !this.isMe(user) && this.isGranted([this.permissions.USER_DELETE, this.permissions.USER_DELETE_OWN], user);
      },
      formatDateTime(date) {
        return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
      },
      ringDash(done, total, radius) {
        let circumference = 2 * Math.PI * radius;
        let length = total ? circumference * Math.min(done / total, 1) : 0;

        return length + ' ' + circumference;
      },
      setStatus(status) {
        this.filters.status = status;
        this.loadContent();
      },
      selectSort(sort) {
        // Same column flips the direction; a new column starts descending
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          this.selectedDirection = 'desc';
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page) {
        let self = this;
        this.currentPage = page || 1;

        piincHttp.get('users', { params:
          {
            page: this.currentPage,
            per_page: this.selectedPerPage,
            sort: this.selectedSort || undefined,
            order: this.selectedDirection || undefined,
            keyword: this.filters.keywordValue || undefined,
            role: this.filters.role ? this.filters.role.id : undefined,
            authentication: this.filters.authentication ? this.filters.authentication.value : undefined,
            access_status: this.filters.status !== 'all' ? this.filters.status : undefined,
            with_access: 1,
            client: this.getClient.id
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      loadSummary() {
        let self = this;

        piincHttp.get('users/access-summary', { params: { client: this.getClient.id } }).then(function(response) {
          self.summary = response.data;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page);
      },
      perPageChanged(perPage) {
        this.selectedPerPage = perPage.value;
        this.$store.commit('storePerPage', this.selectedPerPage);
        this.loadContent(1);
      },
      lockSwitch(user, lock) {
        let self = this;

        piincHttp.patch('users/' + user.slug + '/lock', { lock: lock, client: this.getClient.slug }).then(function() {
          self.reload();
        }, function(error) {
          self.$toastr('error', (error.data && error.data.message) || self.$t('users_page.error'));
        });
      },
      openEdit(user) {
        let role = this.roleOf(user);

        this.editingUser = user;
        this.$refs.edit.open(user, role ? Object.assign({}, role, { name_translated: this.$t('maintenance.users.role.' + this.$options.filters.slugify(role.name)) }) : null);
      },
      resendInvite(user) {
        let self = this;
        let send = function() {
          piincHttp.post('users/' + user.slug + '/invite/resend', { client: self.getClient.slug }).then(function() {
            self.$toastr('success', self.$t('users_page.resend_success', { email: user.email }));
            self.reload();
          }, function(error) {
            let message = error.data && error.data.message;
            self.$toastr('error', message === 'Invite sent recently.' ? self.$t('users_page.resend_recent') : (message || self.$t('users_page.error')));
          });
        };

        // SendGrid drops emails to addresses on its suppression list, so warn before trying again
        if (user.invite && user.invite.status === 'dropped') {
          this.$bvModal.msgBoxConfirm(this.$t('users_page.resend_dropped_text'), {
            title: this.$t('users_page.resend_dropped_title'),
            okTitle: this.$t('users_page.resend_invite'),
            cancelTitle: this.$t('administration.users.create.cancel'),
            modalClass: 'v2-modal',
            centered: true
          }).then(function(confirmed) {
            if (confirmed) {
              send();
            }
          });
        } else {
          send();
        }
      },
      showConfirmDeleteModal(user) {
        this.itemToDelete = user;
        this.$refs['delete-user'].show();
      },
      hideConfirmDeleteModal() {
        this.$refs['delete-user'].hide();
      },
      confirmDeleteUser(user) {
        let self = this;

        piincHttp.delete('clients/' + this.getClient.slug + '/users/' + user.slug).then(function() {
          self.hideConfirmDeleteModal();
          self.$refs.edit.close();
          self.reload();
          self.$toastr('success', self.$t('users_page.remove.success', { name: user.name }));
        }, function() {});
      },
      showMergeModal() {
        this.error = '';
        this.selectedFirstUser = null;
        this.selectedSecondUser = null;
        this.keepUser = 1;
        this.buttonDisabled = false;
        this.loadUsers();
        this.$refs['merge-users'].show();
      },
      hideMergeModal() {
        this.$refs['merge-users'].hide();
      },
      selectMergeUser(side, user) {
        if (side === 'from') {
          this.selectedFirstUser = user;
        } else {
          this.selectedSecondUser = user;
        }
        this.loadUsers();
      },
      loadUsers(query) {
        let self = this;

        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50,
            // For the 120px photos on the merge cards
            with_access: 1
          }
        }).then(function(response) {
          self.users = _.filter(response.data.items, function(object) {
            return !(self.selectedFirstUser && object.id == self.selectedFirstUser.id) && !(self.selectedSecondUser && object.id == self.selectedSecondUser.id);
          });

          if (!self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true});
          }
        }, function() {});
      },
      mergeFormSubmit() {
        let self = this;

        if (!(this.selectedFirstUser && this.selectedFirstUser.id && this.selectedSecondUser && this.selectedSecondUser.id)) {
          this.error = this.$t('maintenance.users.merge_users_error');
          return;
        }

        this.buttonDisabled = true;
        piincHttp.post('users/merge', {
          from_user: this.selectedFirstUser.id,
          to_user: this.selectedSecondUser.id,
          client: this.getClient.slug,
          keep_user: this.keepUser
        }).then(function() {
          self.$toastr('success', self.$t('maintenance.users.merge_users_success'));
          self.buttonDisabled = false;
          self.reload();
          self.hideMergeModal();
        }, function(error) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        });
      },
      setRoles() {
        let self = this;
        // A client has no partition admins and a partition no client admins
        let hidden = this.getClient && this.getClient.parent ? 'Client admin' : 'Partition admin';

        this.roles = _.filter(this.getClientRoles, function(role) {
          return role.name != hidden;
        });
        _.forEach(this.roles, function(role) {
          role.name_translated = self.$t('maintenance.users.role.' + self.$options.filters.slugify(role.name));
        });
      },
      getGuide(code) {
        return _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled;
        });
      },
      loadGuides(code) {
        let self = this;

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            scope: code,
            language: 'en',
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides = response.data.items;
          }
        }, function() {});
      },
      // "/" jumps to the search, unless the user is typing somewhere
      focusSearch(event) {
        let tag = event.target && event.target.tagName;

        if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !(event.target && event.target.isContentEditable)) {
          event.preventDefault();
          this.$refs.search.focus();
        }
      }
    },
    beforeRouteLeave(to, from, next) {
      this.$store.commit('storeActiveFilters', { type: 'users', filters: this.filters });
      this.$store.commit('storePerPage', this.selectedPerPage);
      next();
    },
    created() {
      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }
      if (this.getFilters.users && this.getFilters.users.status) {
        this.filters = Object.assign({}, this.filters, this.getFilters.users);
      }
      this.setRoles();
      if (this.getClient && this.getClient.slug) {
        this.reload();
        this.loadGuides('users_and_owners_list');
      }
    },
    mounted() {
      window.addEventListener('keydown', this.focusSearch);
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.focusSearch);
    }
  }
</script>
